import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, MapPin } from 'lucide-react';
import { PageShell } from '../components/PageShell';
import { DateNav } from '../components/DateNav';
import { ScheduleTable } from '../components/ScheduleTable';
import { Countdown } from '../components/Countdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format "2026-04-13" / "2026-04-21" -> "13 - 21 April 2026"
const formatRange = (s, e) => {
  if (!s || !e) return '';
  const sd = new Date(s + 'T00:00:00');
  const ed = new Date(e + 'T00:00:00');
  const month = ed.toLocaleString('en-US', { month: 'long' });
  const year = ed.getFullYear();
  const sameMonth = sd.getMonth() === ed.getMonth() && sd.getFullYear() === ed.getFullYear();
  if (sameMonth) return `${sd.getDate()} - ${ed.getDate()} ${month} ${year}`;
  return `${sd.getDate()} ${sd.toLocaleString('en-US', { month: 'short' })} – ${ed.getDate()} ${ed.toLocaleString('en-US', { month: 'short' })} ${year}`;
};

const STATUS_BADGE = {
  opened: { label: 'Happening Now', cls: 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30' },
  upcoming: { label: 'Upcoming', cls: 'bg-secondary-main/20 text-secondary-main border-secondary-main/30' },
  archived: { label: 'Archived', cls: 'bg-white/[0.04] text-white/40 border-white/[0.08]' },
};

const yearOf = (s) => {
  const d = s.start_date || s.end_date || '';
  return d ? d.slice(0, 4) : 'TBA';
};

// Big horizontal festival card: wide poster on the left, dark info panel
// (date range + location) on the right — styled after the Red Dragon
// "Series Schedule" layout.
const ScheduleCard = ({ series }) => {
  const badge = STATUS_BADGE[series.status] || STATUS_BADGE.archived;
  return (
    <a
      href={`/series/${series.slug}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`series-card-${series.slug}`}
      className="group grid grid-cols-1 md:grid-cols-2 rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.02] hover:border-primary-main/40 transition-colors"
    >
      {/* Poster */}
      <div className="relative aspect-[2/1] md:aspect-auto md:min-h-[240px] overflow-hidden">
        {series.poster_url ? (
          <img src={series.poster_url} alt={series.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center px-6 text-center" style={{ background: 'linear-gradient(135deg, #1a0808 0%, #2d0e0e 55%, #120505 100%)' }}>
            <span className="font-heading text-5xl sm:text-6xl font-black tracking-wider text-primary-main/25">RDPT+</span>
            <span className="mt-2 font-heading text-[11px] uppercase tracking-[0.35em] text-white/25 line-clamp-1">{series.name}</span>
          </div>
        )}
        {series.accessory_url && (
          <img src={series.accessory_url} alt="" className="absolute bottom-3 right-3 w-20 h-20 object-contain opacity-90" />
        )}
        <div className="absolute top-4 left-4">
          <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-heading uppercase tracking-[0.2em] border ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
      </div>

      {/* Info panel */}
      <div
        className="relative flex flex-col justify-center gap-5 p-8 sm:p-10"
        style={{ background: 'linear-gradient(135deg, rgba(24,10,10,0.55) 0%, rgba(8,4,4,0.92) 100%)' }}
      >
        <h3 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-white group-hover:text-primary-main transition-colors">
          {series.name}
        </h3>
        <div className="flex items-center gap-3">
          <Calendar className="w-6 h-6 text-secondary-main shrink-0" />
          <span className="font-mono text-lg sm:text-xl text-white tracking-wide">{formatRange(series.start_date, series.end_date)}</span>
        </div>
        {series.location && (
          <div className="flex items-center gap-3">
            <MapPin className="w-6 h-6 text-primary-main shrink-0" />
            <span className="font-body text-base sm:text-lg text-white/80">{series.location}</span>
          </div>
        )}
        {series.status === 'upcoming' && (
          <Countdown startDate={series.start_date} variant="card" testid={`countdown-${series.slug}`} />
        )}
        <span className="mt-1 inline-flex items-center gap-1.5 font-heading text-[11px] uppercase tracking-[0.25em] text-white/40 group-hover:text-primary-main transition-colors">
          View schedule <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </a>
  );
};

const ScheduleHero = () => (
  <section className="relative overflow-hidden border-b border-white/[0.04]" data-testid="series-schedule-hero">
    <div className="absolute top-0 left-1/4 w-[600px] h-[300px] rounded-full opacity-[0.10]" style={{ background: 'radial-gradient(circle, #b91c1c 0%, transparent 70%)' }} />
    <div className="absolute bottom-0 right-1/4 w-[500px] h-[260px] rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(circle, #d4af37 0%, transparent 70%)' }} />
    <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-16 text-center">
      <p className="font-heading text-[10px] tracking-[0.4em] uppercase text-primary-main/80 mb-4">Red Dragon Poker Tour</p>
      <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.9]" data-testid="series-schedule-title">
        <span className="text-white">Series </span>
        <span style={{ background: 'linear-gradient(90deg,#d4af37,#f5e6a8,#d4af37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Schedule</span>
      </h1>
    </div>
  </section>
);

export const SeriesListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/series`).then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Group by year (newest first). Within a year: live > upcoming > archived,
  // then most-recent start date first.
  const statusRank = { opened: 0, upcoming: 1, archived: 2 };
  const years = [...new Set(items.map(yearOf))].sort((a, b) => b.localeCompare(a));
  const byYear = years.map(y => ({
    year: y,
    list: items
      .filter(i => yearOf(i) === y)
      .sort((a, b) => {
        const sr = (statusRank[a.status] ?? 3) - (statusRank[b.status] ?? 3);
        if (sr !== 0) return sr;
        return (b.start_date || '').localeCompare(a.start_date || '');
      }),
  }));

  return (
    <PageShell>
      <ScheduleHero />
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {loading ? (
          <p className="mt-16 text-center text-white/40">Loading…</p>
        ) : items.length === 0 ? (
          <div className="mt-16 text-center py-16 border border-white/[0.04] rounded-2xl">
            <p className="font-body text-sm text-white/40">No series scheduled yet.</p>
          </div>
        ) : (
          byYear.map(({ year, list }) => (
            <div key={year} className="mt-14 first:mt-12" data-testid={`series-year-${year}`}>
              <h2 className="text-center font-heading text-2xl sm:text-3xl font-bold uppercase tracking-[0.15em] text-white/90 mb-8">
                {year} Events
              </h2>
              <div className="space-y-8">
                {list.map(s => <ScheduleCard key={s.id} series={s} />)}
              </div>
            </div>
          ))
        )}
      </section>
    </PageShell>
  );
};

// ===== DETAIL PAGE =====
const enumerateDates = (start, end) => {
  const out = [];
  if (!start || !end) return out;
  // Build dates using local YYYY-MM-DD (NOT toISOString) so KST/positive-UTC
  // browsers don't lose a day to the UTC shift.
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const d = new Date(start + 'T00:00:00');
  const ed = new Date(end + 'T00:00:00');
  while (d <= ed) {
    out.push(fmt(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
};

const computeTodayKst = () => {
  const now = new Date();
  const kst = new Date(now.getTime() + (9 * 60 - now.getTimezoneOffset()) * 60 * 1000);
  return `${kst.getFullYear()}-${String(kst.getMonth() + 1).padStart(2, '0')}-${String(kst.getDate()).padStart(2, '0')}`;
};

// SeriesDetailPage — clean billboard layout: small RDPT+ banner, centered
// "DAILY TOURNAMENT SCHEDULE" + active date, date scrollbar, single table.
// NO TopNav, NO breadcrumb, NO duplicate "DAILY SCHEDULE" header.
export const SeriesDetailPage = () => {
  const { slug } = useParams();
  const [series, setSeries] = useState(null);
  const [settings, setSettings] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [dateCounts, setDateCounts] = useState({});
  const [loadingTours, setLoadingTours] = useState(false);
  // While true the page follows "today" (KST) and flips to the next day at
  // 00:00 KST. Picking another date in the strip pauses following.
  const [followToday, setFollowToday] = useState(true);

  useEffect(() => {
    axios.get(`${API}/series/${slug}`).then(r => {
      setSeries(r.data);
      document.title = `${r.data.name} — Daily Schedule`;
      const dates = enumerateDates(r.data.start_date, r.data.end_date);
      const todayKst = computeTodayKst();
      if (dates.includes(todayKst)) setSelectedDate(todayKst);
      else if (dates.length > 0) setSelectedDate(r.data.status === 'archived' ? dates[dates.length - 1] : dates[0]);
    }).catch(() => setNotFound(true));
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(e => console.error('Failed to load settings:', e));
    axios.get(`${API}/tournaments/dates`).then(r => {
      setDateCounts(Object.fromEntries(r.data.map(d => [d.date, d.tournament_count])));
    }).catch(e => console.error('Failed to load tournament dates:', e));
  }, [slug]);

  // Midnight (KST) rollover — checked every 30s.
  useEffect(() => {
    if (!series || !followToday) return undefined;
    const tick = () => {
      const today = computeTodayKst();
      const dates = enumerateDates(series.start_date, series.end_date);
      if (dates.includes(today)) setSelectedDate(prev => (prev !== today ? today : prev));
    };
    const id = setInterval(tick, 30 * 1000);
    return () => clearInterval(id);
  }, [series, followToday]);

  const pickDate = (d) => {
    setFollowToday(d === computeTodayKst());
    setSelectedDate(d);
  };

  useEffect(() => {
    if (!selectedDate) return undefined;
    let cancelled = false;
    const run = async (showSpinner) => {
      if (showSpinner) setLoadingTours(true);
      try {
        const r = await axios.get(`${API}/tournaments/live`, { params: { date: selectedDate } });
        if (!cancelled) setTournaments(r.data);
      } catch (e) {
        console.warn('Live refresh failed, keeping previous data:', e.message);
      } finally {
        if (!cancelled && showSpinner) setLoadingTours(false);
      }
    };
    // Initial load: show spinner. Subsequent refreshes: silent so the screen doesn't flicker.
    run(true);
    const interval = setInterval(() => run(false), 60000); // 60s auto-refresh
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedDate]);

  if (notFound) return <div className="min-h-screen bg-background-base flex items-center justify-center text-white/40">Series not found.</div>;
  if (!series) return <div className="min-h-screen bg-background-base flex items-center justify-center text-white/40">Loading…</div>;

  const dates = enumerateDates(series.start_date, series.end_date).map(d => ({ date: d, tournament_count: dateCounts[d] || 0 }));
  const bgUrl = settings.hero_bg_url || settings.bg_image_url || '';

  const formatDateHeading = (d) => {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: '#080404',
        backgroundImage: bgUrl ? `url(${bgUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
      data-testid="series-detail-page"
    >
      {/* No dark overlay — background shows at full brightness like the billboard. */}

      <div className="relative">
        {/* Centered DAILY TOURNAMENT SCHEDULE — the active date is already
            highlighted in the scrollbar below, so we don't repeat it here. */}
        {/* Top gap scales with the background (cover): ~300px of a 1920x1080 image
            stays clear so the brand artwork is never covered by the heading. */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pb-2 text-center" style={{ paddingTop: 'max(21vh, 11vw)' }}>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[0.06em] text-white" data-testid="series-schedule-heading">
            Daily Tournament Schedule
          </h2>
          <div className="mt-3 mx-auto h-[2px] w-32" style={{ background: 'linear-gradient(to right, transparent, #d4af37, transparent)' }} />
          <div className="mt-2 inline-flex items-center gap-1.5 font-heading text-[10px] uppercase tracking-[0.25em] text-white/45">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            Live — auto-updates every 60s
          </div>
        </div>

        {/* Date scrollbar */}
        <div className="max-w-[1400px] mx-auto px-0 sm:px-6 lg:px-8 pt-4">
          <DateNav
            dates={dates}
            selectedDate={selectedDate}
            onDateSelect={pickDate}
            lockedDate={null}
            onLockToggle={() => {}}
          />
        </div>

        {/* Tournament table — compact (no internal DAILY SCHEDULE duplicate) */}
        <div className="max-w-[1400px] mx-auto px-2 sm:px-6 lg:px-8 pb-14">
          <ScheduleTable tournaments={tournaments} loading={loadingTours} selectedDate={selectedDate || ''} compact />
        </div>
      </div>
    </div>
  );
};

// SeriesBillboardPage — kept as an alias so existing bookmarks of /board still
// work. Just redirects to the canonical /series/:slug page.
export const SeriesBillboardPage = () => {
  const { slug } = useParams();
  return <Navigate to={`/series/${slug}`} replace />;
};
